import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  AngebotsId,
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  HTTP_METHOD,
  ModalType,
  ResponseType,
} from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { RisikoortRequestData, RisikoortResponseData, VorschadenArt } from '@ps20/bff-models';
import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { IAppState } from '../appState';
import { VwgActions } from '../vwgActions';

const globalActions = new GlobalActions();
const vwgActions = new VwgActions();

export enum RisikoortRequestEvent {
  RISIKOORT_REQUEST_SUCCESSFUL = 'RISIKOORT_REQUEST_SUCCESSFUL',
  RISIKOORT_REQUEST_ERROR = 'RISIKOORT_REQUEST_ERROR',
}

export function* watchRisikoortRequested(): SagaIterator {
  yield takeEvery(VwgActions.RISIKOORT_DATA_REQUESTED, safeSaga(risikoortDataRequested, App.VWG_V2));
  yield takeEvery(
    RisikoortRequestEvent.RISIKOORT_REQUEST_SUCCESSFUL,
    safeSaga(risikoortRequestSuccessful, App.VWG_V2),
  );
  yield takeEvery(
    RisikoortRequestEvent.RISIKOORT_REQUEST_ERROR,
    safeSaga(risikoortRequestError, App.VWG_V2),
  );
}

export function* risikoortRequestSuccessful(
  successAction: DoRequestResponse<RisikoortResponseData>,
): SagaIterator {
  const store: IAppState = yield select();

  yield put(vwgActions.showZumAngebotLoadingSpinner(false));
  yield put(
    globalActions.updateRisikoortZuerszone(successAction.payload.zuerszone, store?.angebotsId),
  );
  if (successAction.payload.vorschaeden > 0) {
    yield put(
      vwgActions.updateElementarVorschadenFromRisikoortService(successAction.payload.vorschaeden),
    );
  }
  if (successAction.payload.notExistingVorschaeden?.length > 0) {
    yield put(
      vwgActions.showVorschaedenErsatzgeschaeft({
        elementar: successAction.payload.notExistingVorschaeden.includes(VorschadenArt.ELEMENTAR),
        leitungswasser: successAction.payload.notExistingVorschaeden.includes(VorschadenArt.LEITUNGSWASSER),
        sturmhagel: successAction.payload.notExistingVorschaeden.includes(VorschadenArt.STURM_HAGEL),
        feuer: successAction.payload.notExistingVorschaeden.includes(VorschadenArt.FEUER),
      }),
    );
  }
}

export function* risikoortRequestError(): SagaIterator {
  yield put(vwgActions.showZumAngebotLoadingSpinner(false));
  yield put(globalActions.requestModal(ModalType.RISIKOORT_REQUESTED_ERROR));
}

export function* risikoortDataRequested(action: GlobalAction<AngebotsId>): SagaIterator {
  const store: IAppState = yield select();
  const isErsatzgeschaeft =
    store.vertragsoptionen.vorvertragsNummern?.length > 0 && !store.vertragsoptionen.beibehalten;

  if (isErsatzgeschaeft) {
    const angebotsIdInStore = store.angebotsId;
    const { appId, angebotsId } = action.payload;

    if (appId === App.VWG_V2 && angebotsId === angebotsIdInStore) {
      return yield call(risikoortSaga);
    }
  }
}

export function* risikoortSaga(): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();

  yield put(vwgActions.showZumAngebotLoadingSpinner(true));

  yield call(
    globalPut.put.bind(globalPut),
    globalActions.doRequest({
      appId: App.VWG_V2,
      method: HTTP_METHOD.POST,
      retry: {
        delay: 1000,
        times: 3,
      },
      responseType: ResponseType.APPLICATION_JSON,
      body: {
        plz: state.risikoort.plz,
        ort: state.risikoort.ort,
        strasse: state.risikoort.strasse,
        hausnummer: state.risikoort.hausnummer,
        postlandschl: state.risikoort.land.wert || 'DE',
        beginndatum: dayjs(state.vertragsoptionen.beginnDatum, DATEFORMAT_LOCAL_STATE).format(
          DATEFORMAT_SHORT_ISO,
        ),
        vorvertragsnummern: state.vertragsoptionen.vorvertragsNummern.map(vv => vv.vertragsnummer),
      } as RisikoortRequestData,
      successEvent: RisikoortRequestEvent.RISIKOORT_REQUEST_SUCCESSFUL,
      errorEvent: RisikoortRequestEvent.RISIKOORT_REQUEST_ERROR,
      url: 'privatschutz/api/risikoort',
    }),
  );
}
